import {
  getActivePushpinAttributes,
  getArePushpinSnapshotsEnabled,
  getCurrentPreviewIssueAttributes,
  getCurrentPreviewIssueId,
  getCurrentSheets,
  getLocationsTreeNodesDictionary,
  getPendingPushpinThumbnails
} from '../selectors';
import {getPushpinLocationId, shouldCreatePushpin, updateIssueWithSnapshotUrn} from '../../utils/pushpin-helpers';
import {getLinkedDocument, getNonEmpties, hasPermittedAttribute, isIssueInSheet} from '../../utils/issues-helpers';
import {updateIssueAction} from './issues-actions';
import {ISSUE_ATTRIBUTE_NAMES} from '../../constants/issue-attribute-names';
import featureFlags, {FEATURE_FLAGS} from '../../utils/featureFlags';
import {getToken} from '../../utils/getToken';
import lodashGet from 'lodash/get';
import {uploadThumbnail} from '../../utils/data-transfer-helpers';
import {locationChangeNotification} from '../../utils/notificationHelpers';

export const PUSHPIN_WORKFLOW_ENABLED = 'PUSHPIN_WORKFLOW_ENABLED';
export const PUSHPIN_WORKFLOW_BEGIN = 'PUSHPIN_WORKFLOW_BEGIN';
export const PUSHPIN_WORKFLOW_END = 'PUSHPIN_WORKFLOW_END';
export const PUSHPIN_ATTRIBUTES_SET = 'PUSHPIN_ATTRIBUTES_SET';
export const VIEWER_PUSHPIN_THUMBNAIL_CREATED = 'VIEWER_PUSHPIN_THUMBNAIL_CREATED';
export const PUSHPIN_THUMBNAIL_UPLOADED = 'PUSHPIN_THUMBNAIL_UPLOADED';
export const PUSHPIN_THUMBNAIL_CLEAR_PENDING = 'PUSHPIN_THUMBNAIL_CLEAR_PENDING';

export const setPushpinExtensionAction = pushpinExtension => (dispatch, getState, {pushpinsControl}) => {
  if (pushpinsControl && !pushpinsControl.pushpinExtension) {
    if (pushpinExtension && pushpinExtension.updatePattern) {
      // Add "quality issues" statuses to "issues" type pushpins -
      // in unified issues all "quality_issues" pushpins will have "issues" type
      pushpinExtension.updatePattern('issues-not_approved', 80, '#ff495c');
      pushpinExtension.updatePattern('issues-not_approved-selected', 80, '#ff495c');
      pushpinExtension.updatePattern('issues-not_approved-movable', 80, '#ff495c');

      pushpinExtension.updatePattern('issues-ready_to_inspect', 80, '#7a77d9');
      pushpinExtension.updatePattern('issues-ready_to_inspect-selected', 80, '#7a77d9');
      pushpinExtension.updatePattern('issues-ready_to_inspect-movable', 80, '#7a77d9');

      pushpinExtension.updatePattern('issues-work_completed', 80, '#087cd9');
      pushpinExtension.updatePattern('issues-work_completed-selected', 80, '#087cd9');
      pushpinExtension.updatePattern('issues-work_completed-movable', 80, '#087cd9');

      pushpinExtension.updatePattern('issues-in_dispute', 80, '#ff495c');
      pushpinExtension.updatePattern('issues-in_dispute-selected', 80, '#ff495c');
      pushpinExtension.updatePattern('issues-in_dispute-movable', 80, '#ff495c');

      pushpinExtension.updatePattern('issues-void', 80, '#bcc9d1');
      pushpinExtension.updatePattern('issues-void-selected', 80, '#bcc9d1');
      pushpinExtension.updatePattern('issues-void-movable', 80, '#bcc9d1');
    }
    pushpinsControl.setPushpinExtension(pushpinExtension);
  }
};

export const enablePushpinWorkflowAction = enabled => ({
  type: PUSHPIN_WORKFLOW_ENABLED,
  payload: enabled
});

export const beginPushpinWorkflow = () => ({
  type: PUSHPIN_WORKFLOW_BEGIN
});

export const endPushpinWorkflow = () => ({
  type: PUSHPIN_WORKFLOW_END
});

export const setPushpinAttributesAction = attributes => (dispatch, getState, {pushpinsControl}) => {
  if (pushpinsControl && attributes === null) {
    const activePushpinAttributes = getActivePushpinAttributes(getState());
    if (activePushpinAttributes && activePushpinAttributes.isNew) {
      pushpinsControl.clearSinglePushpin(activePushpinAttributes);
    }
  }
  dispatch({
    type: PUSHPIN_ATTRIBUTES_SET,
    payload: {attributes}
  });
};

export const batchCreatePushpinAction = (issues, currentSheet) => (dispatch, getState, {pushpinsControl}) => {
  if (pushpinsControl) {
    const state = getState();

    if (typeof state.filterCallback === 'function') {
      // Call the optional filter callback passed by the hosting app. The client could for example
      // filter all issues by objectId.
      issues = issues.filter(state.filterCallback);
    }

    const currentSheets = currentSheet ? [currentSheet] : getCurrentSheets(state);
    issues.forEach(issue => {
      if (shouldCreatePushpin(issue, pushpinsControl, currentSheets)) {
        pushpinsControl.createPushpin(issue);
      }
    });
  }
};

export const updatePushpinAction = pushpin => (dispatch, getState, {pushpinsControl}) => {
  if (pushpinsControl) {
    pushpinsControl.updatePushpin({...pushpin});
  }
};

export const batchClearPushpinAction = issues => (dispatch, getState, {pushpinsControl}) => {
  if (pushpinsControl) {
    const currentSheets = getCurrentSheets(getState());
    issues.forEach(issue => {
      // TODO: Do we actually need this condition? https://jira.autodesk.com/browse/SCCOM-9417
      if (isIssueInSheet(issue, currentSheets)) {
        pushpinsControl.clearSinglePushpin(issue);
      }
    });
  }
};

export const clearPushpinAction = issue => (dispatch, getState, {pushpinsControl}) => {
  if (pushpinsControl) {
    const currentSheets = getCurrentSheets(getState());
    // TODO: Same as above
    if (isIssueInSheet(issue, currentSheets)) {
      pushpinsControl.clearSinglePushpin(issue);
    }
  }
};

export const selectPushpinAction = issue => (dispatch, getState, {pushpinsControl}) => {
  if (pushpinsControl) {
    const state = getState();
    if (shouldCreatePushpin(issue, pushpinsControl, getCurrentSheets(state))) {
      pushpinsControl.createPushpin(issue);
    }
    const canRelocatePushpin = hasPermittedAttribute(issue, ISSUE_ATTRIBUTE_NAMES.LINKED_DOCUMENTS);
    pushpinsControl.selectPushpin(issue.id, canRelocatePushpin);
  }
};

export const deselectPushpinsAction = () => (dispatch, getState, {pushpinsControl}) => {
  if (pushpinsControl) {
    pushpinsControl.deselectPushpins();
  }
};

export const startDropPushpinAction = status => (dispatch, getState, {pushpinsControl}) => {
  if (pushpinsControl) {
    pushpinsControl.startDropPushpin(status);
  }
};

export const endDropPushpinAction = () => (dispatch, getState, {pushpinsControl}) => {
  if (pushpinsControl) {
    pushpinsControl.endDropPushpin();
  }
};

export const pushpinThumbnailUploaded = ({pushpinId, snapshotUrn}) => ({
  type: PUSHPIN_THUMBNAIL_UPLOADED,
  payload: {pushpinId, snapshotUrn}
});

export const clearPendingThumbnail = pushpinId => (dispatch, getState) => {
  if (getArePushpinSnapshotsEnabled(getState())) {
    dispatch({
      type: PUSHPIN_THUMBNAIL_CLEAR_PENDING,
      payload: pushpinId
    });
  }
};

export const updateCurrentIssuePushpinAttributes = issuePushpin => (dispatch, getState) => {
  const state = getState();
  const currentIssue = getCurrentPreviewIssueAttributes(state);
  const {createdAt, createdBy, ...currentLinkedDocumentAttributes} = getLinkedDocument(currentIssue) ?? {
    details: {}
  };
  const [currentSheet] = getCurrentSheets(state) ?? [];
  const document = getNonEmpties({
    ...(featureFlags.get(FEATURE_FLAGS.ISSUES_V2) ? currentLinkedDocumentAttributes : {}),
    type: issuePushpin.position.z ? 'TwoDVectorPushpin' : 'TwoDRasterPushpin',
    details: getNonEmpties({
      ...(currentSheet?.sheetGuid
        ? {
          viewable: {
            id: currentSheet.sheetGuid,
            name: currentSheet.sheetName,
            is3D: currentSheet.is3D,
            guid: currentSheet.sheetGuid,
            viewableId: currentSheet.viewableId
          }
        }
        : {}),
      position: issuePushpin.position,
      objectId: issuePushpin.objectId,
      viewerState: issuePushpin.viewerState,
      externalId: issuePushpin.externalId
    })
  });

  const {attributesVersion} = issuePushpin;
  if (attributesVersion) {
    Object.assign(document.details.viewerState, {attributesVersion});
  }
  const updateValues = {[ISSUE_ATTRIBUTE_NAMES.LINKED_DOCUMENTS]: [document]};
  if (issuePushpin.locationIds?.length) {
    updateValues[ISSUE_ATTRIBUTE_NAMES.LOCATION_ID] = getPushpinLocationId(issuePushpin);
  }
  const locationsTreeNodesDictionary = getLocationsTreeNodesDictionary(state);
  dispatch(
    updateIssueAction(currentIssue, updateValues, {
      onSuccess: () =>
        featureFlags.get(FEATURE_FLAGS.UPDATE_LOCATION_WITH_PUSHPIN) &&
        dispatch(
          locationChangeNotification(
            locationsTreeNodesDictionary[updateValues[ISSUE_ATTRIBUTE_NAMES.LOCATION_ID]]?.name
          )
        )
    })
  );
};

export const viewerPushpinThumbnailCreated = ({pushpinAttributes, thumbnail, issueId}) => (
  dispatch,
  getState,
  {issuesV2Api, jsonApi}
) => {
  if (!featureFlags.get(FEATURE_FLAGS.STOP_PUSHPIN_THUMBNAIL_UPLOAD) && getArePushpinSnapshotsEnabled(getState())) {
    const existingIssueId = issueId;
    Promise.all([issuesV2Api.createOSSObject(), getToken({getState, dispatch})])
      .then(([{urn}, token]) => uploadThumbnail(urn, token, thumbnail))
      .then(uploadResponse => {
        // If we already saved the relevant issue, update it with the thumbnail's snapshot URN.
        // Otherwise, we just save the snapshot URN in our store for using it later (once the issue is created)
        const snapshotUrn = lodashGet(uploadResponse, 'data.objectId');
        const issueIdToUpdate =
          existingIssueId ?? lodashGet(getPendingPushpinThumbnails(getState()), `${pushpinAttributes.id}.issueId`);

        if (issueIdToUpdate) {
          updateIssueWithSnapshotUrn(
            jsonApi,
            issuesV2Api,
            dispatch,
            issueIdToUpdate,
            snapshotUrn,
            pushpinAttributes.id
          );
        } else {
          dispatch(pushpinThumbnailUploaded({pushpinId: pushpinAttributes.id, snapshotUrn}));
        }
      })
      .catch(() => {
        // Thumbnail upload failed
        dispatch(clearPendingThumbnail(pushpinAttributes.id));
      });

    dispatch({
      type: VIEWER_PUSHPIN_THUMBNAIL_CREATED,
      payload: {pushpinAttributes, thumbnail, issueId}
    });
  }
};

export const viewerExistingIssuePushpinThumbnailCreated = ({pushpinAttributes, thumbnail}) => (dispatch, getState) => {
  const state = getState();
  if (getArePushpinSnapshotsEnabled(state)) {
    const issueId = getCurrentPreviewIssueId(state);
    dispatch(viewerPushpinThumbnailCreated({pushpinAttributes, thumbnail, issueId}));
  }
};
